import { Check, ChevronDown, ChevronsUpDown, ChevronUp, Copy } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Button as AriaButton,
  Cell,
  Column,
  Row,
  Table,
  TableBody,
  TableHeader,
} from 'react-aria-components';
import { cx } from '../cx.ts';

/**
 * Copyable technical value (address, TXID, IBAN, tracking ID): LTR-isolated mono text,
 * copy icon morphs to a check for 1.5s and announces «کپی شد» to screen readers.
 */
export function CopyField({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return (
    <div className={cx('rounded-md border border-line bg-surface-2 px-3.5 py-3', className)}>
      <div className="mb-1 text-xs text-fg-muted">{label}</div>
      <div className="flex items-center gap-2">
        <bdi className="hx-ltr min-w-0 flex-1 break-all font-mono text-[13px] leading-6 text-fg">
          {value}
        </bdi>
        <AriaButton
          aria-label={`کپی ${label}`}
          onPress={async () => {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            window.clearTimeout(timer.current);
            timer.current = window.setTimeout(() => setCopied(false), 1500);
          }}
          className="grid size-9 shrink-0 place-items-center rounded-sm text-fg-muted hover:bg-surface-3 hover:text-fg outline-hidden data-[focus-visible]:outline-2 data-[focus-visible]:outline-solid data-[focus-visible]:outline-focus cursor-pointer"
        >
          {copied ? (
            <Check aria-hidden className="size-4 text-positive" />
          ) : (
            <Copy aria-hidden className="size-4" />
          )}
        </AriaButton>
      </div>
      <span role="status" className="sr-only">
        {copied ? 'کپی شد' : ''}
      </span>
    </div>
  );
}

export interface DataTableColumn<T> {
  id: string;
  header: string;
  cell: (row: T) => ReactNode;
  /** Numeric/technical columns align to the logical end and use tabular numerals. */
  numeric?: boolean;
  isRowHeader?: boolean;
  /** Header becomes a sort control (aria-sort); the caller sorts (server or client). */
  sortable?: boolean;
  /** Optional min width, e.g. '9rem' — keeps dense admin columns readable at 1024–1280. */
  minWidth?: string;
  /**
   * Column priority: hidden below this viewport width (px). Use only for columns whose data is also
   * available elsewhere (e.g. the record's detail page) — never for the only copy of a value.
   */
  hideBelow?: number;
}

export interface TableSort {
  column: string;
  direction: 'ascending' | 'descending';
}

function useViewportWidth(): number {
  const [w, setW] = useState(() => (typeof window === 'undefined' ? 1440 : window.innerWidth));
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return w;
}

/** Tracks horizontal overflow and scroll position of an RTL scroll container. */
function useHorizontalScroll() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [s, setS] = useState({ overflow: false, moreStart: false, moreEnd: false });
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const pos = Math.abs(el.scrollLeft); // RTL: 0 at the start (right), negative towards the left
    setS({ overflow: max > 1, moreStart: pos > 1, moreEnd: pos < max - 1 });
  }, []);
  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    el.addEventListener('scroll', measure, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', measure);
    };
  }, [measure]);
  return { ref, ...s, measure };
}

/**
 * Data table (React Aria Table: keyboard grid navigation, row headers, aria-sort). Admin queues use
 * `density="compact"`, `maxHeight` (scroll container with a sticky header), server sort and pagination.
 * Narrow viewports (1024–1280): `stickyFirst` keeps the identity column visible, edge shadows and a
 * hint show that more columns exist, and the scroll region is keyboard-focusable. `loading` keeps the
 * layout stable with skeleton rows and `aria-busy`; errors are rendered by the caller — a failed load
 * is never shown as an empty table.
 */
export function DataTable<T extends { id: string }>({
  label,
  columns: allColumns,
  rows,
  empty,
  sort,
  onSortChange,
  onRowAction,
  loading,
  density = 'comfortable',
  maxHeight,
  stickyFirst,
  className,
}: {
  label: string;
  columns: DataTableColumn<T>[];
  rows: T[];
  empty: ReactNode;
  sort?: TableSort | undefined;
  onSortChange?: ((s: TableSort) => void) | undefined;
  onRowAction?: ((id: string) => void) | undefined;
  loading?: boolean | undefined;
  density?: 'comfortable' | 'compact';
  maxHeight?: string | undefined;
  stickyFirst?: boolean | undefined;
  className?: string | undefined;
}) {
  const compact = density === 'compact';
  const vw = useViewportWidth();
  const columns = allColumns.filter((c) => !c.hideBelow || vw >= c.hideBelow);
  const { ref: scrollRef, overflow, moreStart, moreEnd, measure } = useHorizontalScroll();
  useEffect(() => measure(), [columns.length, rows.length, measure]);
  const sticky = (i: number, header: boolean) =>
    stickyFirst && i === 0
      ? cx(
          'sticky start-0 z-[2]',
          header ? 'bg-surface-2' : 'bg-surface-1',
          moreStart && 'shadow-[-8px_0_10px_-8px_var(--hx-scrim)]',
        )
      : undefined;
  return (
    <div className={cx('relative', className)} data-table-wrap>
      <div
        ref={scrollRef}
        className={cx(
          'rounded-lg border border-line bg-surface-1',
          maxHeight ? 'overflow-auto' : 'overflow-x-auto',
          'outline-hidden focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-focus',
        )}
        style={maxHeight ? { maxHeight } : undefined}
        aria-busy={loading || undefined}
        {...(overflow
          ? {
              tabIndex: 0,
              role: 'region',
              'aria-label': `${label} — پیمایش افقی`,
              'data-scrollable': '',
            }
          : {})}
      >
        <Table
          aria-label={label}
          className="w-full border-collapse text-sm"
          {...(sort ? { sortDescriptor: sort } : {})}
          {...(onSortChange
            ? {
                onSortChange: (d: { column: string | number; direction: TableSort['direction'] }) =>
                  onSortChange({ column: String(d.column), direction: d.direction }),
              }
            : {})}
          {...(onRowAction ? { onRowAction: (k: string | number) => onRowAction(String(k)) } : {})}
        >
          <TableHeader
            className={cx(
              'bg-surface-2 text-xs text-fg-muted',
              maxHeight && 'sticky top-0 z-10 shadow-[0_1px_0_var(--hx-border)]',
            )}
          >
            {columns.map((c, i) => (
              <Column
                key={c.id}
                id={c.id}
                isRowHeader={c.isRowHeader}
                allowsSorting={c.sortable}
                style={c.minWidth ? { minWidth: c.minWidth } : undefined}
                className={cx(
                  'px-4 font-semibold whitespace-nowrap outline-hidden',
                  compact ? 'h-10' : 'h-11',
                  c.numeric ? 'text-end' : 'text-start',
                  c.sortable && 'cursor-pointer hover:text-fg',
                  sticky(i, true),
                  'data-[focus-visible]:outline-2 data-[focus-visible]:outline-solid data-[focus-visible]:outline-focus data-[focus-visible]:-outline-offset-2',
                )}
              >
                {({ sortDirection }) => (
                  <span className="inline-flex items-center gap-1">
                    {c.header}
                    {c.sortable &&
                      (sortDirection === 'ascending' ? (
                        <ChevronUp aria-hidden className="size-3.5 text-fg" />
                      ) : sortDirection === 'descending' ? (
                        <ChevronDown aria-hidden className="size-3.5 text-fg" />
                      ) : (
                        <ChevronsUpDown aria-hidden className="size-3.5 opacity-50" />
                      ))}
                  </span>
                )}
              </Column>
            ))}
          </TableHeader>
          <TableBody
            items={loading ? [] : rows}
            renderEmptyState={() =>
              loading ? (
                <div className="flex flex-col gap-3 p-4" data-table-loading>
                  {[0, 1, 2, 3].map((i) => (
                    <span
                      key={i}
                      aria-hidden
                      className="hx-skeleton block h-5 rounded-sm bg-[linear-gradient(90deg,var(--hx-surface2)_25%,var(--hx-surface3)_50%,var(--hx-surface2)_75%)] bg-[length:200%_100%] animate-[hx-shimmer_1.4s_linear_infinite]"
                    />
                  ))}
                  <span className="sr-only">در حال بارگذاری</span>
                </div>
              ) : (
                <div className="py-10">{empty}</div>
              )
            }
          >
            {(row) => (
              <Row
                id={row.id}
                className={cx(
                  'group/row border-t border-line text-fg outline-hidden hover:bg-surface-2',
                  onRowAction && 'cursor-pointer',
                  'data-[focus-visible]:outline-2 data-[focus-visible]:outline-solid data-[focus-visible]:outline-focus data-[focus-visible]:-outline-offset-2',
                )}
              >
                {columns.map((c, i) => (
                  <Cell
                    key={c.id}
                    className={cx(
                      'px-4 whitespace-nowrap',
                      compact ? 'h-11 text-[13px]' : 'h-13',
                      c.numeric && 'hx-num text-end',
                      sticky(i, false),
                      stickyFirst && i === 0 && 'group-hover/row:bg-surface-2',
                    )}
                  >
                    {c.cell(row)}
                  </Cell>
                ))}
              </Row>
            )}
          </TableBody>
        </Table>
      </div>
      {/* Edge shadows: content continues beyond the visible edge (RTL: end = left). */}
      {moreEnd && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-px end-px w-8 rounded-e-lg bg-[linear-gradient(to_left,var(--hx-scrim),transparent)] opacity-60"
          data-scroll-shadow="end"
        />
      )}
      {overflow && (
        <p className="m-0 mt-1.5 text-[11.5px] text-fg-subtle" data-scroll-hint>
          ستون‌های بیشتری در دسترس است؛ جدول را افقی پیمایش کنید (Shift + چرخ ماوس یا کلیدهای جهت).
        </p>
      )}
    </div>
  );
}
